import { useState } from 'react';
import type { Task } from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { DatePicker } from '../../components/ui/DatePicker.js';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog.js';

export function EditTaskDialog({
  projectId,
  task,
  open,
  onClose,
}: {
  projectId: string;
  task: Task;
  open: boolean;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState(task.acceptanceCriteria.join('\n'));
  const [estimateHours, setEstimateHours] = useState(task.estimateHours?.toString() ?? '');
  const [labels, setLabels] = useState(task.labels.join(', '));
  const [dueDate, setDueDate] = useState<string | null>(task.dueDate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.patch(`/projects/${projectId}/tasks/${task.id}`, {
        title: title.trim(),
        description,
        acceptanceCriteria: acceptanceCriteria
          .split('\n')
          .map((c) => c.trim())
          .filter(Boolean),
        estimateHours: estimateHours.trim() ? Number(estimateHours) : null,
        labels: labels
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean),
        dueDate,
      });
      onClose();
    } catch {
      setError('Could not save changes. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Phones lean on the Dialog's own dvh-capped scroll; an inner 80vh one would ignore the
          on-screen keyboard and nest a second scroller. */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 md:max-h-[80vh] md:overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Edit task</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-title">Title</Label>
          <Input
            id="task-title"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-description">Description</Label>
          <Textarea
            id="task-description"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-acceptance">Acceptance criteria</Label>
          <Textarea
            id="task-acceptance"
            rows={4}
            placeholder="One per line"
            value={acceptanceCriteria}
            onChange={(e) => setAcceptanceCriteria(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="task-estimate">Estimate (hrs)</Label>
            <Input
              id="task-estimate"
              type="number"
              min={0}
              value={estimateHours}
              onChange={(e) => setEstimateHours(e.target.value)}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="task-due">Due date</Label>
            <DatePicker
              id="task-due"
              value={dueDate}
              onChange={setDueDate}
              onClear={() => setDueDate(null)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="task-labels">Labels</Label>
          <Input
            id="task-labels"
            placeholder="Comma-separated"
            value={labels}
            onChange={(e) => setLabels(e.target.value)}
          />
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="default" disabled={submitting || !title.trim()}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
